import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Edit2, Trash2, Check, X, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const LabelConfig = () => {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const SECTIONS = ['FOB', 'Inhouse', 'Combo', 'Greige', 'SFG', 'Dispatch', 'Other'];

  const [formData, setFormData] = useState({
    config_name: '',
    section: '',
    label_width_mm: 25.0,
    label_height_mm: 25.0,
    qr_code_x: 6.25,
    qr_code_y: 0.4,
    qr_code_size: 4,
    usn_text_x: 5.0,
    usn_text_y: 13.75,
    usn_text_font_size: 1,
    sort_text_x: 5.0,
    sort_text_y: 17.5,
    sort_text_font_size: 1,
    is_default: false
  });

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/label-configs/list');
      setConfigs(response.data.configs || []);
    } catch (error) {
      console.error('Error fetching configs:', error);
      toast.error('Failed to load configurations');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (config = null) => {
    if (config) {
      setEditingConfig(config);
      setFormData({ ...config, section: config.section || '' });
    } else {
      setEditingConfig(null);
      setFormData({
        config_name: '',
        section: '',
        label_width_mm: 25.0,
        label_height_mm: 25.0,
        qr_code_x: 6.25,
        qr_code_y: 0.4,
        qr_code_size: 4,
        usn_text_x: 5.0,
        usn_text_y: 13.75,
        usn_text_font_size: 1,
        sort_text_x: 5.0,
        sort_text_y: 17.5,
        sort_text_font_size: 1,
        is_default: false
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (editingConfig) {
        await apiClient.put(`/label-configs/update/${editingConfig.config_id}`, formData);
        toast.success('Configuration updated successfully');
      } else {
        await apiClient.post('/label-configs/create', formData);
        toast.success('Configuration created successfully');
      }
      
      setShowDialog(false);
      fetchConfigs();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to save configuration';
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetDefault = async (configId) => {
    try {
      await apiClient.post(`/label-configs/set-default/${configId}`);
      toast.success('Set as default configuration');
      fetchConfigs();
    } catch (error) {
      toast.error('Failed to set default configuration');
    }
  };

  const handleDelete = async (configId, configName) => {
    if (!window.confirm(`Delete configuration "${configName}"?`)) return;
    
    try {
      await apiClient.delete(`/label-configs/delete/${configId}`);
      toast.success('Configuration deleted');
      fetchConfigs();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete configuration';
      toast.error(errorMsg);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin/dashboard')}
              className="text-[#F9FAFB] hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Label Configuration</h1>
              <p className="text-sm text-[#E8DCC8]">Manage printer label settings</p>
            </div>
          </div>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-[#2D5F4F] hover:bg-[#234A3D]"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Config
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-4 border-[#2D5F4F] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : configs.length === 0 ? (
          <Card className="border-2 border-dashed border-[#E5DFD0]">
            <CardContent className="py-12 text-center">
              <Settings className="w-16 h-16 mx-auto text-[#E5DFD0] mb-4" />
              <p className="text-[#5A5F6E]">No configurations created yet</p>
              <Button
                onClick={() => handleOpenDialog()}
                className="mt-4 bg-[#2D5F4F] hover:bg-[#234A3D]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First Config
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {configs.map((config) => (
              <Card key={config.config_id} className={`border-2 ${config.is_default ? 'border-[#2D5F4F] bg-[#2D5F4F]/5' : 'border-[#E5DFD0]'}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2 flex-wrap">
                        {config.config_name}
                        {config.is_default && (
                          <span className="text-xs bg-[#2D5F4F] text-white px-2 py-0.5 rounded">Default</span>
                        )}
                        {config.section && (
                          <span className="text-xs bg-[#E8DCC8] text-[#1A1F2E] px-2 py-0.5 rounded">
                            {config.section}
                          </span>
                        )}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        {config.label_width_mm}mm × {config.label_height_mm}mm
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenDialog(config)}
                        className="text-[#2D5F4F] hover:bg-[#2D5F4F]/10"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(config.config_id, config.config_name)}
                        className="text-red-500 hover:bg-red-50"
                        disabled={config.is_default}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="grid grid-cols-4 gap-2">
                      <span className="font-semibold text-[#5A5F6E]">QR Code:</span>
                      <span>X: {config.qr_code_x}mm</span>
                      <span>Y: {config.qr_code_y}mm</span>
                      <span>Size: {config.qr_code_size}</span>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      <span className="font-semibold text-[#5A5F6E]">USN Text:</span>
                      <span>X: {config.usn_text_x}mm</span>
                      <span>Y: {config.usn_text_y}mm</span>
                      <span>Font: {config.usn_text_font_size}</span>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      <span className="font-semibold text-[#5A5F6E]">Sort Text:</span>
                      <span>X: {config.sort_text_x}mm</span>
                      <span>Y: {config.sort_text_y}mm</span>
                      <span>Font: {config.sort_text_font_size}</span>
                    </div>
                  </div>
                  {!config.is_default && (
                    <Button
                      onClick={() => handleSetDefault(config.config_id)}
                      variant="outline"
                      className="w-full mt-4 border-[#2D5F4F] text-[#2D5F4F] hover:bg-[#2D5F4F]/10"
                      size="sm"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Set as Default
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingConfig ? 'Edit Configuration' : 'Create New Configuration'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Config Name + Section */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="config_name">Configuration Name *</Label>
                <Input
                  id="config_name"
                  value={formData.config_name}
                  onChange={(e) => handleInputChange('config_name', e.target.value)}
                  placeholder="e.g., Inhouse 25x25"
                  required
                  className="mt-1"
                  data-testid="label-config-name-input"
                />
              </div>
              <div>
                <Label htmlFor="section">Section / Module</Label>
                <select
                  id="section"
                  value={formData.section || ''}
                  onChange={(e) => handleInputChange('section', e.target.value)}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="label-config-section-select"
                >
                  <option value="">— None —</option>
                  {SECTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Label Dimensions */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="label_width_mm">Label Width (mm) *</Label>
                <Input
                  id="label_width_mm"
                  type="number"
                  step="0.1"
                  value={formData.label_width_mm}
                  onChange={(e) => handleInputChange('label_width_mm', parseFloat(e.target.value))}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="label_height_mm">Label Height (mm) *</Label>
                <Input
                  id="label_height_mm"
                  type="number"
                  step="0.1"
                  value={formData.label_height_mm}
                  onChange={(e) => handleInputChange('label_height_mm', parseFloat(e.target.value))}
                  required
                  className="mt-1"
                />
              </div>
            </div>

            {/* QR Code Settings */}
            <div className="border-2 border-[#E5DFD0] rounded-lg p-4 space-y-4">
              <h3 className="font-semibold text-[#1A1F2E]">QR Code Position</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="qr_code_x">X-Axis (mm) *</Label>
                  <Input
                    id="qr_code_x"
                    type="number"
                    step="0.1"
                    value={formData.qr_code_x}
                    onChange={(e) => handleInputChange('qr_code_x', parseFloat(e.target.value))}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="qr_code_y">Y-Axis (mm) *</Label>
                  <Input
                    id="qr_code_y"
                    type="number"
                    step="0.1"
                    value={formData.qr_code_y}
                    onChange={(e) => handleInputChange('qr_code_y', parseFloat(e.target.value))}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="qr_code_size">QR Size (1-10) *</Label>
                  <Input
                    id="qr_code_size"
                    type="number"
                    min="1"
                    max="10"
                    value={formData.qr_code_size}
                    onChange={(e) => handleInputChange('qr_code_size', parseInt(e.target.value))}
                    required
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* USN Text Settings */}
            <div className="border-2 border-[#E5DFD0] rounded-lg p-4 space-y-4">
              <h3 className="font-semibold text-[#1A1F2E]">USN Text Position</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="usn_text_x">X-Axis (mm) *</Label>
                  <Input
                    id="usn_text_x"
                    type="number"
                    step="0.1"
                    value={formData.usn_text_x}
                    onChange={(e) => handleInputChange('usn_text_x', parseFloat(e.target.value))}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="usn_text_y">Y-Axis (mm) *</Label>
                  <Input
                    id="usn_text_y"
                    type="number"
                    step="0.1"
                    value={formData.usn_text_y}
                    onChange={(e) => handleInputChange('usn_text_y', parseFloat(e.target.value))}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="usn_text_font_size">Font Size (1-5) *</Label>
                  <Input
                    id="usn_text_font_size"
                    type="number"
                    min="1"
                    max="5"
                    value={formData.usn_text_font_size}
                    onChange={(e) => handleInputChange('usn_text_font_size', parseInt(e.target.value))}
                    required
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Sort Text Settings */}
            <div className="border-2 border-[#E5DFD0] rounded-lg p-4 space-y-4">
              <h3 className="font-semibold text-[#1A1F2E]">Sort No Text Position</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="sort_text_x">X-Axis (mm) *</Label>
                  <Input
                    id="sort_text_x"
                    type="number"
                    step="0.1"
                    value={formData.sort_text_x}
                    onChange={(e) => handleInputChange('sort_text_x', parseFloat(e.target.value))}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="sort_text_y">Y-Axis (mm) *</Label>
                  <Input
                    id="sort_text_y"
                    type="number"
                    step="0.1"
                    value={formData.sort_text_y}
                    onChange={(e) => handleInputChange('sort_text_y', parseFloat(e.target.value))}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="sort_text_font_size">Font Size (1-5) *</Label>
                  <Input
                    id="sort_text_font_size"
                    type="number"
                    min="1"
                    max="5"
                    value={formData.sort_text_font_size}
                    onChange={(e) => handleInputChange('sort_text_font_size', parseInt(e.target.value))}
                    required
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Set as Default */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_default"
                checked={formData.is_default}
                onCheckedChange={(checked) => handleInputChange('is_default', checked)}
              />
              <Label htmlFor="is_default" className="cursor-pointer">
                Set as default configuration
              </Label>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                disabled={isSubmitting}
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                {editingConfig ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LabelConfig;